import {
  APPRENTICESHIP_PRODUCT_CODES,
  CalendarFeature,
  featureMapping,
  Features,
  isVbpProductCode,
  Price,
  Product,
  PRODUCT_CALENDAR_PERIOD_MAP,
  PRODUCT_FEATURES,
  PRODUCT_OFFERING_GROUP_MAP,
  PRODUCT_OFFERING_PLATFORMS,
  PRODUCT_TRANSLATIONS,
  ProductCode,
  ProductFeature,
  ProductFeatures,
  ProductOfferingPlatform,
  ProductPlatformGroup,
  PublicationCalendarBenefit,
  Variant,
} from '@mkp/shared/models';
import { DictionaryNum } from '@ngrx/entity';
import { Currencies } from '@mkp/shared/models';

const referenceQuantity = 1 as const;

export function isCalendarProductFeature(
  productFeature: ProductFeature
): productFeature is ProductFeature & { id: CalendarFeature } {
  return PRODUCT_CALENDAR_PERIOD_MAP[productFeature.id as CalendarFeature] !== undefined;
}

export const isPlatformFeature = (
  productFeature: ProductFeature
): productFeature is ProductFeature & {
  id: ProductOfferingPlatform;
} => PRODUCT_OFFERING_PLATFORMS.includes(productFeature.id as ProductOfferingPlatform);

/**
 * Checks if a product is a lite product (Jobs Lite or JobUp Lite)
 * Lite products have special 7 paid + 24 free days structure
 * @param productCode The product code to check
 * @returns true if the product is JOBSOFFER22XS or JOBUP24LITE
 */
export function isLiteProduct(
  productCode: ProductCode
): productCode is ProductCode.JOBSOFFER22XS | ProductCode.JOBUP24LITE {
  return productCode === ProductCode.JOBSOFFER22XS || productCode === ProductCode.JOBUP24LITE;
}

export const getFirstVariant = (product: Pick<Product, 'variants' | 'code'>): Variant => {
  if (!product.variants || product.variants.length === 0) {
    throw new Error(`Variant not found for product code ${product.code}`);
  }

  // Find by code pattern or return first variant
  const variant =
    product.variants.find((variant: Variant) => variant.code === `${product.code}-variant-0`) ||
    product.variants[0];

  return variant;
};

export const getReferencePrice = (product: Product | undefined): number => {
  return getPriceForQuantity(product as Product, referenceQuantity).current;
};

export const getCurrency = (product: Pick<Product, 'variants' | 'code'>): Currencies => {
  const variant = getFirstVariant(product);
  return variant.currencyCode as Currencies;
};

export const getPricesPerQuantity = (
  product: Pick<Product, 'variants' | 'code'>
): {
  quantity: number;
  price: Price;
}[] => {
  const variant = getFirstVariant(product);

  // Array structure only
  return variant.prices.map(({ quantity, price }) => ({
    quantity: Number(quantity),
    price: {
      current: price,
      currency: variant.currencyCode as Currencies,
    },
  }));
};

export const getDistinctPricesPerQuantity = (
  product: Pick<Product, 'variants' | 'code'>
): DictionaryNum<Price> =>
  Object.values(
    getPricesPerQuantity(product).reduceRight(
      (acc, { quantity, price }) => ({
        ...acc,
        [price.current]: quantity,
      }),
      {} as Record<number, number>
    )
  ).reduceRight(
    (acc, quantity) => ({
      ...acc,
      [quantity]: getPriceForQuantity(product, quantity),
    }),
    {}
  );

export const getPriceForQuantity = (
  product: Pick<Product, 'variants' | 'code'>,
  quantity: number
): Price => {
  const variant = getFirstVariant(product);

  // Find the best matching price for the given quantity
  // Uses bulk pricing: find the highest quantity threshold <= requested quantity
  const validPrices = variant.prices
    .filter((p) => p.quantity <= quantity)
    .sort((a, b) => b.quantity - a.quantity);

  const selectedPrice = validPrices[0] || variant.prices[0];

  return {
    current: selectedPrice.price,
    currency: variant.currencyCode as Currencies,
  };
};

export const getPercentDiscountForQuantity = (
  product: Pick<Product, 'variants' | 'code'>,
  quantity: number
) => {
  const price = getPriceForQuantity(product, quantity);
  const priceForOne = getPriceForQuantity(product, 1);

  return 100 - (price.current / priceForOne.current) * 100;
};

/** Undefined when the client carries no feature list for the code, as for a product Ecom ships first. */
export const getPartialFeatures = ({ code }: Pick<Product, 'code'>): ProductFeature[] | undefined =>
  PRODUCT_FEATURES.find(({ productCode }: ProductFeatures) => productCode === code)?.features.map(
    (feature: Features): ProductFeature => {
      const { parent, title, description, translation } = featureMapping[feature];

      return { id: feature, parent, title, description, translation };
    }
  );

/** Undefined when the feature list carries no calendar benefit to derive a period from. */
export const getCalendarBenefit = (
  productFeatures: ProductFeature[]
): PublicationCalendarBenefit | undefined => {
  const calendar = productFeatures.find(isCalendarProductFeature);

  return calendar
    ? {
        id: calendar.id,
        period: PRODUCT_CALENDAR_PERIOD_MAP[calendar.id],
        translation: calendar.translation || calendar.title || '',
      }
    : undefined;
};

export const sortByPriceDesc = (p1: Product, p2: Product): number =>
  p1.basePrice < p2.basePrice ? 1 : -1;

export const isPaid = (product: Product): boolean => product.basePrice > 0;
export const isApprenticeship = (product: Product): boolean =>
  APPRENTICESHIP_PRODUCT_CODES.includes(product.code);
export const isFromPlatform = (product: Product, platform: ProductPlatformGroup): boolean =>
  PRODUCT_OFFERING_GROUP_MAP[platform].includes(product.code);

/**
 * Only products priced per application quote a ladder that differs per vacancy.
 * Sending the scope for the rest would alter the one request every checkout
 * depends on, which is not worth doing until Ecom confirms that
 * `/products/{code}` ignores an unrelated `vacancyId`.
 */
export const needsVacancyScope = (productCode: ProductCode): boolean =>
  isVbpProductCode(productCode);

export const getPaidProductsOrderedByPrice =
  (platform: ProductPlatformGroup) =>
  (products: Product[] | null): Product[] | undefined =>
    products
      ?.filter((product) => isFromPlatform(product, platform) && isPaid(product))
      .sort(sortByPriceDesc);

export const getQuantityDiscount = (
  product: Pick<Product, 'variants' | 'code'>,
  quantity: number
): number => {
  const percent = getPercentDiscountForQuantity(product, quantity);
  return Math.round(percent);
};

export const getCartItemLongName = (cartItem: {
  variant: { product: { code: string } };
}): string => {
  const productCode = cartItem.variant.product.code as ProductCode;
  return PRODUCT_TRANSLATIONS[productCode]?.longname || '';
};

/**
 * Converts price from cents (backend storage) to currency units (display format).
 *
 * Backend convention: All prices stored in cents (e.g., 89000 = 890 CHF)
 * Display convention: UI shows currency units (e.g., 890 = 890 CHF)
 *
 * @param priceInCents - Price in cents from backend
 * @returns Price in currency units for display
 */
export const formatPrice = (priceInCents: number): number => priceInCents / 100;

export const getPublicationDaysFromProductCode = (
  productCode: ProductCode | string,
  products: Product[] | null | undefined
): number | undefined => {
  const product = products?.find((p) => p.code === productCode);
  return product?.calendar?.period;
};
